import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, FileText, FileBarChart, Workflow, Menu } from 'lucide-react';

/**
 * iOS-style mobile bottom tab bar (md:hidden) — iter 179 redesign.
 *
 * Visual treatment:
 *  • Frosted glass background (backdrop-blur 22px + saturate)
 *  • Active state: pill-shaped tinted background (Apple Tabs pattern), not a top bar
 *  • Subtle press-scale (active:scale-95) on every tab for tactile feedback
 *  • Copper accent (acountix brand) on the active state
 *  • Safe-area-inset-bottom respected for iPhone gesture bar
 */
export default function MobileBottomNav({ onMoreClick }) {
  const location = useLocation();
  const tabs = [
    { path: '/', icon: LayoutDashboard, label: 'Kezdő', testId: 'mobile-nav-home' },
    { path: '/invoices', icon: FileText, label: 'Számlák', testId: 'mobile-nav-invoices' },
    { path: '/proposals', icon: FileBarChart, label: 'Ajánlat', testId: 'mobile-nav-proposals' },
    { path: '/projects', icon: Workflow, label: 'Projekt', testId: 'mobile-nav-projects' },
  ];

  const isActive = (p) => {
    if (p === '/') return location.pathname === '/';
    return location.pathname === p || location.pathname.startsWith(p + '/');
  };

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="md:hidden fixed bottom-0 left-0 right-0 z-30 flex items-stretch px-2 pt-1.5"
      style={{
        background: 'var(--mobile-nav-bg, rgba(255,255,255,0.78))',
        backdropFilter: 'blur(22px) saturate(180%)',
        WebkitBackdropFilter: 'blur(22px) saturate(180%)',
        borderTop: '1px solid var(--border)',
        paddingBottom: 'calc(env(safe-area-inset-bottom) + 6px)',
        boxShadow: '0 -8px 24px -10px rgba(0,0,0,0.12)',
      }}
    >
      {tabs.map((tab) => {
        const Icon = tab.icon;
        const active = isActive(tab.path);
        return (
          <Link
            key={tab.path}
            to={tab.path}
            data-testid={tab.testId}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 mx-0.5 rounded-2xl transition-all duration-200 active:scale-95"
            style={{
              color: active ? 'var(--accent-copper)' : 'var(--text-secondary)',
              fontWeight: active ? 700 : 500,
              background: active
                ? 'linear-gradient(180deg, rgba(217,119,6,0.14) 0%, rgba(217,119,6,0.08) 100%)'
                : 'transparent',
              boxShadow: active ? 'inset 0 0 0 1px rgba(217,119,6,0.22)' : 'none',
            }}
          >
            <Icon size={22} strokeWidth={active ? 2.4 : 1.85} />
            <span
              className="text-[11px]"
              style={{ letterSpacing: '-0.005em' }}
            >
              {tab.label}
            </span>
          </Link>
        );
      })}
      <button
        type="button"
        onClick={onMoreClick}
        data-testid="mobile-nav-more"
        className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1.5 mx-0.5 rounded-2xl transition-all duration-200 active:scale-95"
        style={{ color: 'var(--text-secondary)', fontWeight: 500, background: 'transparent' }}
        aria-label="Több menü"
      >
        <Menu size={22} strokeWidth={1.85} />
        <span className="text-[11px]" style={{ letterSpacing: '-0.005em' }}>Több</span>
      </button>
    </nav>
  );
}
